import { useMemo, useRef, useState } from 'react'
import GavelRoundedIcon from '@mui/icons-material/GavelRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ImageRoundedIcon from '@mui/icons-material/ImageRounded'
import { useAuth } from '../../context/useAuth'
import { useDisplayName } from '../../hooks/useDisplayName'
import { useTimeline } from '../../context/useTimeline'
import { CountBadge } from '../Common'
import { Pressable } from '../Common/Pressable'
import { IconButton } from '../Common/IconButton'
import { ProofReviewActions } from './ProofReviewActions'

/**
 * Approver console — a floating pill button that shows a numeric badge
 * with the count of checklist items awaiting the current user's approval.
 * Clicking opens an inline panel listing every awaiting item with the
 * shared review actions inline. Renders in BoardExplorer's header on the
 * /console surface, but is safe to embed anywhere the auth + timeline
 * contexts are available.
 */
export const AwaitingApprovalPanel = () => {
  const { currentUser, userProfile } = useAuth()
  const { checklistItems } = useTimeline()
  const displayName = useDisplayName(userProfile ?? currentUser)
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLDivElement | null>(null)

  const pending = useMemo(() => {
    if (!currentUser) return []
    return checklistItems.filter((item) => (
      item.proofApproverId === currentUser.uid
        && item.workflowStatus === 'ASSESSING'
        && item.proofReviewStatus === 'PENDING'
    ))
  }, [checklistItems, currentUser])

  const reviewer = currentUser ? { uid: currentUser.uid, displayName: displayName || currentUser.email || 'Approver' } : null

  if (!currentUser || pending.length === 0) {
    return null
  }

  return (
    <div className="awaitingApprovalWrap" ref={anchorRef}>
      <Pressable
        type="button"
        className={`awaitingApprovalPill ${open ? 'isOpen' : ''}`}
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <GavelRoundedIcon fontSize="small" />
        <span>Awaiting your approval</span>
        <span aria-label={`${pending.length} pending`}>
          <CountBadge value={pending.length} max={99} className="awaitingApprovalBadge" />
        </span>
      </Pressable>

      {open ? (
        <aside className="awaitingApprovalPanel" role="region" aria-label="Items awaiting your approval">
          <header>
            <div>
              <strong>Awaiting your approval</strong>
              <small>{pending.length} item{pending.length === 1 ? '' : 's'} to review</small>
            </div>
            <IconButton
              size="sm"
              variant="tertiary"
              onClick={() => setOpen(false)}
              label="Close approvals panel"
              icon={<CloseRoundedIcon fontSize="small" />}
            />
          </header>
          <ul className="awaitingApprovalList">
            {pending.map((item) => (
              <li key={item.id} className="awaitingApprovalItem">
                <div className="awaitingApprovalItemHead">
                  <strong>{item.title}</strong>
                  <small>Submitted by {item.proofUploadedByName ?? 'a helper'}</small>
                </div>
                {item.proofFileUrl ? (
                  <a
                    className="awaitingApprovalItemProof"
                    href={item.proofFileUrl}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Open proof file"
                  >
                    <ImageRoundedIcon fontSize="small" />
                    <span>{item.proofFileName ?? 'View proof'}</span>
                  </a>
                ) : null}
                <ProofReviewActions item={item} actor={reviewer} compact />
              </li>
            ))}
          </ul>
        </aside>
      ) : null}
    </div>
  )
}
